import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TEAM_PLANET_REQUIRED_BUILDING_LEVEL, type Planet } from '@kaeliux/shared';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { convertToTeamPlanet } from '../services/teamPlanet.service';
import { usePlanetStore } from '../store/planetStore';
import { useAuthStore } from '../store/authStore';
import { useMyAlliance } from '../hooks/useMyAlliance';
import { useTeamPlanet } from '../hooks/useTeamPlanet';

function errorMessage(error: unknown, fallback: string): string {
    return error instanceof Error && error.message ? error.message : fallback;
}

interface Props {
    planet: Planet;
}

/**
 * Planet-page CTA to convert THIS planet into the alliance's Team planet
 * (2026-08-14 gameplay ruling: "The option appears on the planet page of
 * all leader's planets when criteria is met"). Leader only, and only when
 * the alliance has no Team planet yet and SOME planet of the leader's
 * (not necessarily this one) has a Command Center building at level
 * {@link TEAM_PLANET_REQUIRED_BUILDING_LEVEL}. "No active flights to or
 * from this planet" is enforced server-side — this card doesn't
 * pre-check it, and surfaces the server's rejection message if violated.
 */
export default function TeamPlanetConversionCard({ planet }: Props) {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const planets = usePlanetStore((s) => s.planets);
    const loadPlanets = usePlanetStore((s) => s.loadPlanets);
    const setActivePlanet = usePlanetStore((s) => s.setActivePlanet);
    const { profile } = useAuthStore();
    const [error, setError] = useState<string | null>(null);

    const allianceQuery = useMyAlliance();
    const teamPlanetQuery = useTeamPlanet({ enabled: allianceQuery.data?.viewerRole === 'leader' });

    const convertMutation = useMutation({
        mutationFn: () => convertToTeamPlanet(planet.id),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['teamPlanet'] });
            await queryClient.invalidateQueries({ queryKey: ['alliance'] });
            // The converted planet leaves the personal list, so the switcher's
            // active planet falls back to the homeworld straight away.
            if (profile?.id) {
                await loadPlanets(profile.id);
                if (profile.mainPlanetId) await setActivePlanet(profile.mainPlanetId, profile.id);
            }
            navigate('/team-planet');
        },
        onError: (err) => setError(errorMessage(err, 'Failed to convert this planet into the Team planet.')),
    });

    if (allianceQuery.data?.viewerRole !== 'leader') return null;
    if (teamPlanetQuery.data) return null; // alliance already has one
    // The main planet can never become the Team planet (mirrors the delete-
    // planet gate, `PlanetPage.tsx`'s `planet.id !== profile.mainPlanetId`)
    // — also closes off converting your ONLY planet, since a single-planet
    // account's one planet is always its main one.
    if (profile?.mainPlanetId && planet.id === profile.mainPlanetId) return null;

    const hasQualifyingBuilding = planets.some(
        (p) => (p.buildings.government?.level ?? 0) >= TEAM_PLANET_REQUIRED_BUILDING_LEVEL,
    );
    if (!hasQualifyingBuilding) return null;

    return (
        <Card
            className="border-status-warning/40"
            header={<h3 className="text-title text-content-primary">Convert to Team Planet</h3>}
        >
            <p className="text-content-muted text-label">
                Turn this planet into your alliance&apos;s shared Team planet. It resets entirely (buildings,
                resources, ships) and can never be converted back — only do this on a planet you&apos;re ready to
                give up.
            </p>
            {error && <p className="text-status-danger text-label" role="alert">{error}</p>}
            <Button
                type="button"
                variant="primary"
                size="sm"
                data-testid="convert-to-team-planet-button"
                loading={convertMutation.isPending}
                onClick={() => { setError(null); convertMutation.mutate(); }}
            >
                Convert to Team Planet
            </Button>
        </Card>
    );
}
